import React from 'react';
import { Link } from 'react-router-dom';

import { NAVIGATE_PATH as PATH } from '../../../helpers/constants';
import { TOpsClassDetailEx } from '../../../requests/opsRegistrationsExtra';
import { TOpsClassesMeta } from '../../../types';
import { Card, labelClass } from '../ui';
import { fieldClass, STATUSES, TClassForm, TSetField, TYPES } from './shared';

type Props = {
  form: TClassForm;
  set: TSetField;
  isNew: boolean;
  cancelling: boolean;
  existing?: TOpsClassDetailEx;
  meta?: TOpsClassesMeta;
};

/** Name, type, status, course, and the deal fields read through from the opportunity. */
const DetailsSection: React.FC<Props> = ({
  form,
  set,
  isNew,
  cancelling,
  existing,
  meta,
}) => (
  <Card title="Details">
    <div className="grid grid-cols-1 gap-4 p-5 md:grid-cols-2">
      <div className="md:col-span-2">
        <label className={labelClass}>Class name</label>
        <input
          className={fieldClass}
          value={form.name}
          onChange={(e) => set('name')(e.target.value)}
        />
      </div>
      <div>
        <label className={labelClass}>Type</label>
        <select
          className={fieldClass}
          value={form.classType}
          onChange={(e) => set('classType')(e.target.value)}
        >
          {TYPES.map((t) => (
            <option key={t}>{t}</option>
          ))}
        </select>
      </div>
      <div>
        <label className={labelClass}>Course status</label>
        <select
          className={fieldClass}
          value={form.status}
          onChange={(e) => set('status')(e.target.value)}
        >
          {STATUSES.map((st) => (
            <option key={st}>{st}</option>
          ))}
        </select>
      </div>
      {cancelling && (
        <div className="md:col-span-2">
          <label className="flex items-center gap-2 text-sm text-gray-five">
            <input
              type="checkbox"
              className="ops-check h-4 w-4 accent-blue-primary"
              checked={form.notifyLearners}
              onChange={(e) => set('notifyLearners')(e.target.checked)}
            />
            Email the learners whose seats were open
          </label>
          <div className="mt-1 text-xs text-gray-bold">
            Uses the Cancellation Learnit Email template; quiet accounts are not
            emailed.
          </div>
        </div>
      )}
      <div className="md:col-span-2">
        <label className={labelClass}>Course</label>
        <select
          className={fieldClass}
          value={form.productId}
          onChange={(e) => set('productId')(e.target.value)}
        >
          <option value="">— none —</option>
          {meta?.products.map((prod) => (
            <option key={prod.id} value={prod.id}>
              {prod.name}
              {prod.family ? ` · ${prod.family}` : ''}
            </option>
          ))}
        </select>
        {form.productId && (
          <Link
            className="mt-1 inline-block text-xs text-blue-primary hover:underline"
            to={`${PATH.opsCatalogue}?q=${encodeURIComponent(
              meta?.products.find((pr) => pr.id === form.productId)?.name ?? '',
            )}`}
          >
            open in the catalogue ↗
          </Link>
        )}
      </div>
      {!isNew && existing && (
        <div className="md:col-span-2 grid grid-cols-2 gap-x-6 gap-y-2 rounded-lg border border-white-three bg-grey-50 p-3 text-xs md:grid-cols-4">
          <div>
            <div className={labelClass}>Program</div>
            {existing.programId ? (
              <Link
                className="text-blue-primary hover:underline"
                to={`${PATH.opsPrograms}?id=${existing.programId}`}
              >
                {existing.programNumber ?? existing.programName ?? 'open'}
              </Link>
            ) : (
              <span className="text-gray-bold">—</span>
            )}
          </div>
          <div>
            <div className={labelClass}>Opportunity sub-type</div>
            <div>{existing.fromDeal?.subType ?? '—'}</div>
          </div>
          <div>
            <div className={labelClass}>Deal type</div>
            <div>{existing.fromDeal?.dealType ?? '—'}</div>
          </div>
          <div>
            <div className={labelClass}>Learner registration</div>
            <div>{existing.fromDeal?.learnerRegistrationMethod ?? '—'}</div>
          </div>
          <div>
            <div className={labelClass}>TTA opp</div>
            <div>
              {existing.fromDeal
                ? existing.fromDeal.ttaOpp
                  ? 'yes'
                  : 'no'
                : '—'}
            </div>
          </div>
          <div>
            <div className={labelClass}>Created</div>
            <div>
              {existing.createdDate?.slice(0, 10) ?? '—'}
              {existing.createdBy ? ` · ${existing.createdBy}` : ''}
            </div>
          </div>
          <div>
            <div className={labelClass}>Last modified</div>
            <div>
              {existing.lastModifiedDate?.slice(0, 10) ?? '—'}
              {existing.lastModifiedBy ? ` · ${existing.lastModifiedBy}` : ''}
            </div>
          </div>
          <div className="md:col-span-4 text-gray-bold">
            Deal fields read through from the opportunity; HubSpot will own them
            after the CRM move.
          </div>
        </div>
      )}
    </div>
  </Card>
);

export default DetailsSection;
